import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CompaniesAPI from '../../api/companies';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
  },
};

function isRealCompany(company) {
  const id = company?.id;
  return id != null && !String(id).startsWith('__virtual__');
}

export const getters = {
  getCompanies(_state) {
    return _state.records.filter(isRealCompany);
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCompanyById: _state => id => {
    const found = _state.records.find(
      record => record.id === Number(id) || record.id === id
    );
    return found && isRealCompany(found) ? found : undefined;
  },
};

export const actions = {
  revalidate: async function revalidate({ commit }, { newKey }) {
    try {
      const isExistingKeyValid = await CompaniesAPI.validateCacheKey(newKey);
      if (!isExistingKeyValid) {
        const response = await CompaniesAPI.refetchAndCommit(newKey, {
          per_page: 5000,
        });
        const payload = (response.data?.payload || []).filter(isRealCompany);
        const sorted = [...payload].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
        commit(types.SET_COMPANIES, sorted);
      }
    } catch (error) {
      // Ignore error
    }
  },

  get: async function getCompanies({ commit }) {
    commit(types.SET_COMPANY_UI_FLAG, { isFetching: true });
    try {
      // Force fresh data by passing false to bypass cache
      const response = await CompaniesAPI.get(false, { per_page: 5000 });
      const payload = (response.data.payload || []).filter(isRealCompany);
      const sortedCompanies = [...payload].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
      );
      commit(types.SET_COMPANIES, sortedCompanies);
    } catch (error) {
      // Silently handle error
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isFetching: false });
    }
  },

  create: async function createCompanies({ commit }, companyObj) {
    commit(types.SET_COMPANY_UI_FLAG, { isCreating: true });
    try {
      const response = await CompaniesAPI.create(companyObj);
      const company =
        response.data?.payload?.company ||
        response.data?.payload ||
        response.data;
      if (company && isRealCompany(company)) {
        commit(types.ADD_COMPANY, company);
      }
      return company;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isCreating: false });
    }
  },

  update: async function updateCompanies({ commit }, { id, ...updateObj }) {
    commit(types.SET_COMPANY_UI_FLAG, { isUpdating: true });
    try {
      const response = await CompaniesAPI.update(id, updateObj);
      commit(types.EDIT_COMPANY, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async function deleteCompanies({ commit, dispatch }, id) {
    commit(types.SET_COMPANY_UI_FLAG, { isDeleting: true });
    try {
      await CompaniesAPI.delete(id);
      commit(types.DELETE_COMPANY, id);
      await dispatch('get');
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_COMPANY_UI_FLAG, { isDeleting: false });
    }
  },

  // Remove company from list without API call (e.g. virtual company "delete")
  removeFromList({ commit }, id) {
    commit(types.DELETE_COMPANY, id);
  },
};

export const mutations = {
  [types.SET_COMPANY_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_COMPANIES]: MutationHelpers.set,
  [types.ADD_COMPANY]: MutationHelpers.create,
  [types.EDIT_COMPANY]: MutationHelpers.update,
  [types.DELETE_COMPANY](storeState, id) {
    storeState.records = storeState.records.filter(
      record => String(record.id) !== String(id)
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
